/**
 * Speaker Memories Service
 *
 * API functions for speaker memory CRUD operations.
 * Handles the per-partner AI therapist memory feature including
 * listing, viewing, editing (with validation), resetting, and regenerating memories.
 */

import axios from "axios";
import { apiClient } from "./api";
import type {
  SpeakerMemory,
  SpeakerMemoryListResponse,
  SpeakerMemoryValidationError,
} from "../types/speakerMemory";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * List all speaker memories for the current user.
 */
export async function listSpeakerMemories(): Promise<SpeakerMemoryListResponse> {
  const response = await apiClient.get<SpeakerMemoryListResponse>(
    `${API_PREFIX}/speaker-memories/`
  );
  return response.data;
}

/**
 * Get a specific speaker memory by partner ID.
 */
export async function getSpeakerMemory(
  partnerId: string
): Promise<SpeakerMemory> {
  const response = await apiClient.get<SpeakerMemory>(
    `${API_PREFIX}/speaker-memories/${partnerId}`
  );
  return response.data;
}

/**
 * Update a speaker memory's content.
 * Returns the updated memory on success.
 * Throws a SpeakerMemoryValidationError-shaped error on 422 (content flagged).
 */
export async function updateSpeakerMemory(
  partnerId: string,
  content: string,
  force?: boolean
): Promise<SpeakerMemory> {
  try {
    const response = await apiClient.patch<SpeakerMemory>(
      `${API_PREFIX}/speaker-memories/${partnerId}`,
      { content, force }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 422) {
      const detail = error.response.data?.detail;
      if (detail && detail.verdict) {
        const validationError: SpeakerMemoryValidationError = {
          verdict: detail.verdict,
          issues: detail.issues || [],
          suggestion: detail.suggestion || "",
        };
        throw validationError;
      }
    }
    throw error;
  }
}

/**
 * Reset a speaker memory to the AI-generated version.
 */
export async function resetSpeakerMemory(partnerId: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/speaker-memories/${partnerId}`);
}

/**
 * Manually trigger regeneration of a speaker memory.
 */
export async function regenerateSpeakerMemory(
  partnerId: string
): Promise<SpeakerMemory> {
  const response = await apiClient.post<SpeakerMemory>(
    `${API_PREFIX}/speaker-memories/${partnerId}/regenerate`
  );
  return response.data;
}

/**
 * Type guard for validation errors returned by updateSpeakerMemory.
 */
export function isValidationError(
  error: unknown
): error is SpeakerMemoryValidationError {
  return (
    typeof error === "object" &&
    error !== null &&
    "verdict" in error &&
    "issues" in error
  );
}

// --- User Self-Memory ---

/**
 * Get the current user's self-memory.
 */
export async function getUserSelfMemory(): Promise<SpeakerMemory> {
  const response = await apiClient.get<SpeakerMemory>(
    `${API_PREFIX}/speaker-memories/self`
  );
  return response.data;
}

/**
 * Update the user's self-memory content.
 */
export async function updateUserSelfMemory(
  content: string,
  force?: boolean
): Promise<SpeakerMemory> {
  try {
    const response = await apiClient.patch<SpeakerMemory>(
      `${API_PREFIX}/speaker-memories/self`,
      { content, force }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 422) {
      const detail = error.response.data?.detail;
      if (detail && detail.verdict) {
        const validationError: SpeakerMemoryValidationError = {
          verdict: detail.verdict,
          issues: detail.issues || [],
          suggestion: detail.suggestion || "",
        };
        throw validationError;
      }
    }
    throw error;
  }
}

/**
 * Reset user self-memory to the AI-generated version.
 */
export async function resetUserSelfMemory(): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/speaker-memories/self`);
}

/**
 * Manually trigger regeneration of user self-memory.
 */
export async function regenerateUserSelfMemory(): Promise<SpeakerMemory> {
  const response = await apiClient.post<SpeakerMemory>(
    `${API_PREFIX}/speaker-memories/self/regenerate`
  );
  return response.data;
}

// --- Clear Memory (set content to null for fresh regeneration) ---

/**
 * Clear a partner memory (deletes the row so it regenerates fresh).
 */
export async function clearSpeakerMemory(partnerId: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/speaker-memories/${partnerId}`);
}

/**
 * Clear the user's self-memory (deletes the row so it regenerates fresh).
 */
export async function clearUserSelfMemory(): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/speaker-memories/self`);
}

// --- Text Validation / Bias Check ---

export interface TextValidationResult {
  verdict: "APPROVED" | "FLAGGED";
  issues: string[];
  suggestion: string | null;
}

/**
 * Validate text for bias and unfair characterizations.
 */
export async function validateText(
  text: string,
  context:
    | "relationship_description"
    | "reflection_context"
    | "memory_edit" = "memory_edit"
): Promise<TextValidationResult> {
  const response = await apiClient.post<TextValidationResult>(
    `${API_PREFIX}/users/me/validate-text`,
    { text, context }
  );
  return response.data;
}
